import config from '../../config/config';
import { sessionAwareFetch } from './sessionAwareFetch';
import { collectPhotoUrlsFromFlat } from '../../utils/serviceWizardFormData';
import { deriveWizardPhotoFileName } from '../../utils/wizardPhotoUpload';

const apiBase = () => `${config.apimUrl}/service-wizard-request-api`;

function headers(json = true) {
  const h = {
    'Ocp-Apim-Subscription-Key': config.apimKeyWizardRequestService,
  };
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

async function parseError(response) {
  let msg = `HTTP ${response.status}`;
  try {
    const j = await response.json();
    if (j?.error) msg = j.error;
  } catch {
    // ignore
  }
  return msg;
}

export const serviceWizardSearchRequestService = {
  async getPhotoUploadToken(fileName, { clientSessionId } = {}) {
    const params = new URLSearchParams();
    params.set('fileName', fileName);
    if (clientSessionId?.trim()) {
      params.set('clientSessionId', clientSessionId.trim());
    }
    const response = await sessionAwareFetch(
      `${apiBase()}/photo-token-url?${params.toString()}`,
      {
        method: 'GET',
        headers: headers(false),
        credentials: 'include',
      },
    );
    if (!response.ok) {
      throw new Error(await parseError(response));
    }
    return response.json();
  },

  async uploadWizardPhoto(file, fileName, { clientSessionId } = {}) {
    const data = await this.getPhotoUploadToken(fileName, { clientSessionId });
    const uploadResponse = await fetch(data.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': 'application/octet-stream',
        'x-ms-blob-type': 'BlockBlob',
      },
    });
    if (!uploadResponse.ok) {
      throw new Error('Failed to upload wizard photo');
    }
    return data.fileUrl;
  },

  async uploadWizardPhotos(photos, { clientSessionId } = {}) {
    if (!photos?.length) return [];
    return Promise.all(
      photos.map(async (photo) => {
        if (photo?.file) {
          const fileName = deriveWizardPhotoFileName(photo.file);
          return this.uploadWizardPhoto(photo.file, fileName, { clientSessionId });
        }
        return collectPhotoUrlsFromFlat([photo])[0] || '';
      }),
    );
  },

  async create(payload) {
    const response = await sessionAwareFetch(apiBase(), {
      method: 'POST',
      headers: headers(),
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(await parseError(response));
    }
    return response.json();
  },

  /** Outreach summaries sent for a search request — lightweight list/get response fields. */
  async listByAccount() {
    const response = await sessionAwareFetch(apiBase(), {
      method: 'GET',
      headers: headers(false),
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(await parseError(response));
    }
    return response.json();
  },

  async listByClientSessionId(clientSessionId) {
    const params = new URLSearchParams();
    params.set('clientSessionId', clientSessionId);
    const response = await sessionAwareFetch(`${apiBase()}?${params.toString()}`, {
      method: 'GET',
      headers: headers(false),
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(await parseError(response));
    }
    return response.json();
  },


  /**
   * @param {string} id
   * @param {{ clientSessionId?: string | null }} [opts] — when the doc may still be session-keyed, pass the browser client session id.
   * @returns {Promise<object | null>} null when 404
   */
  async getById(id, opts = {}) {
    const params = new URLSearchParams();
    if (opts.clientSessionId?.trim()) {
      params.set('clientSessionId', opts.clientSessionId.trim());
    }
    const q = params.toString();
    const url = q
      ? `${apiBase()}/${encodeURIComponent(id)}?${q}`
      : `${apiBase()}/${encodeURIComponent(id)}`;
    const response = await sessionAwareFetch(url, {
      method: 'GET',
      headers: headers(false),
      credentials: 'include',
    });
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(await parseError(response));
    }
    return response.json();
  },

  async patchRequester(id, body) {
    const response = await sessionAwareFetch(`${apiBase()}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: headers(),
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const msg = await parseError(response);
      const err = new Error(msg);
      err.status = response.status;
      throw err;
    }
    return response.json();
  },

  async patchLinkToAccount(id, clientSessionId) {
    return this.patchRequester(id, {
      linkToAccount: true,
      clientSessionId,
    });
  },

  async sendToBusiness(id, body) {
    const response = await sessionAwareFetch(
      `${apiBase()}/${encodeURIComponent(id)}/send`,
      {
        method: 'POST',
        headers: headers(),
        credentials: 'include',
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) {
      const msg = await parseError(response);
      const err = new Error(msg);
      err.status = response.status;
      throw err;
    }
    return response.json();
  },
};
